// src/pages/conocimiento/components/CategoriasSidebar.tsx
import { useState } from 'react';
import { ChevronRight, ChevronDown, FolderOpen, Folder, Plus } from 'lucide-react';
import { Button } from '@/components/ui';
import { useCategoriasArbol } from '@/hooks/queries/useConocimiento';
import type { CategoriaArbol } from '@/types';

interface CategoriasSidebarProps {
  selectedCategoriaId: string | null;
  onSelectCategoria: (id: string | null) => void;
  onCrearCategoria?: () => void;
}

interface CategoriaItemProps {
  categoria: CategoriaArbol;
  level: number;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}

function CategoriaItem({ categoria, level, selectedId, onSelect }: CategoriaItemProps) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = categoria.hijos && categoria.hijos.length > 0;
  const isSelected = selectedId === categoria.id;

  return (
    <div>
      <button
        onClick={() => onSelect(isSelected ? null : categoria.id)}
        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-gray-100 transition-colors ${
          isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
        }`}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
      >
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="p-0.5 hover:bg-gray-200 rounded"
          >
            {expanded ? (
              <ChevronDown className="h-3 w-3 text-gray-400" />
            ) : (
              <ChevronRight className="h-3 w-3 text-gray-400" />
            )}
          </button>
        ) : (
          <span className="w-4" />
        )}
        {isSelected ? (
          <FolderOpen className="h-4 w-4 text-blue-600" />
        ) : (
          <Folder className="h-4 w-4 text-gray-400" />
        )}
        <span className="truncate flex-1 text-left">{categoria.nombre}</span>
      </button>
      {hasChildren && expanded && (
        <div>
          {categoria.hijos.map((hijo) => (
            <CategoriaItem
              key={hijo.id}
              categoria={hijo}
              level={level + 1}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function CategoriasSidebar({
  selectedCategoriaId,
  onSelectCategoria,
  onCrearCategoria,
}: CategoriasSidebarProps) {
  const { data: categorias, isLoading } = useCategoriasArbol();

  return (
    <div className="w-64 border-r bg-gray-50 flex flex-col h-full">
      <div className="p-4 border-b flex items-center justify-between">
        <h2 className="font-semibold text-gray-900">Categorías</h2>
        {onCrearCategoria && (
          <Button size="sm" variant="ghost" onClick={onCrearCategoria}>
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        <button
          onClick={() => onSelectCategoria(null)}
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm mb-2 ${
            selectedCategoriaId === null
              ? 'bg-blue-50 text-blue-700'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          <FolderOpen className="h-4 w-4" />
          <span>Todos los artículos</span>
        </button>

        {isLoading ? (
          <div className="text-center py-4 text-sm text-gray-500">Cargando...</div>
        ) : categorias && categorias.length > 0 ? (
          <div className="space-y-0.5">
            {categorias.map((cat) => (
              <CategoriaItem
                key={cat.id}
                categoria={cat}
                level={0}
                selectedId={selectedCategoriaId}
                onSelect={onSelectCategoria}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-sm text-gray-500">
            No hay categorías
          </div>
        )}
      </div>
    </div>
  );
}
